import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, CheckCircle2, Plus, Store, FileText, AlertCircle, Loader2 } from 'lucide-react';
import styles from './CreateBill.module.css';

import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';
import {
    PRICE_MODES,
    calculatePricing,
    clampRate,
    hasValidGstin,
    normalisePriceMode,
} from '../../utils/pricingUtils';
import { applyMembershipToItems, normaliseCards, sortMemberships } from './Membershippricing';

import InvoiceHeader from './components/InvoiceHeader/InvoiceHeader';
import InvoiceGrid from './components/InvoiceGrid/InvoiceGrid';
import InvoiceSummary from './components/InvoiceSummary/InvoiceSummary';
import BillActionStrip from './components/BillActionStrip/BillActionStrip';

// Local timezone date (IST stays IST)
const getLocalYYYYMMDD = (dateInput = new Date()) => {
    const d = new Date(dateInput);
    if (Number.isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const todayISO = () => getLocalYYYYMMDD();

// The invoice number is made by the server when a bill starts:
// DD MM YY + store code + this month's sequence (1610262600030025).

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001')
    .replace(/\/api\/v1(?:\/public)?\/?$/, '')
    .replace(/\/+$/, '');

const API_BASE = `${API_ORIGIN}/api/v1/smiify/sm/invoices`;
const CATALOG_API_BASE = `${API_ORIGIN}/api/v1/smiify/sm`;
const SETTINGS_API = `${API_ORIGIN}/api/v1/smiify/sm/settings`;
const CUSTOMER_API = `${API_ORIGIN}/api/v1/smiify/sm/customers`;
const MEMBERSHIP_API = `${API_ORIGIN}/api/v1/smiify/sm/memberships`;

// Route of the membership desk (renew / recharge page). Change if your route is different.
export const MEMBERSHIP_DESK_PATH = '/renew-loyalty';

const DEFAULT_PRICING_SETTINGS = {
    priceMode: PRICE_MODES.EXCLUSIVE,
    gstRate: 0,
    gstBillEnabled: false,
    gstEnabled: false,
    allowServiceOverride: false,
    gstin: '',
    registrationType: 'REGISTERED',
    gstApplicable: false,
    isGstBill: false,
};

const LOYALTY_TYPES = ['package', 'prepaid', 'discount', 'membership'];

const emptyOverallDiscount = () => ({ reasonSelect: '', customReason: '', value: '', type: '%' });
const emptyPayments = () => ({ cash: '', card: '', upi: '', wallet: '', prepaidCards: [], prepaidCardAmount: 0, prepaidCardId: null });

const getEmptyRow = () => ({
    id: Date.now() + Math.random(),
    itemName: '',
    itemType: 'service',
    staff: [],
    consultedBy: [],
    hsn: '',
    qty: 1,
    maxQty: null,
    rate: '',
    discountValue: '',
    discountType: '%',
    gstRate: null,
    taxPercent: null,
    priceMode: PRICE_MODES.EXCLUSIVE,
    priceIncludesGst: false,
    gstApplicable: false,
});

const normaliseInvoiceItem = (item = {}) => ({
    ...getEmptyRow(),
    ...item,
    id: item.id || item._id || Date.now() + Math.random(),
    staff: Array.isArray(item.staff) ? item.staff : [],
    consultedBy: Array.isArray(item.consultedBy) ? item.consultedBy : [],
    gstRate: item.gstRate ?? item.taxPercent ?? null,
    taxPercent: item.taxPercent ?? item.gstRate ?? null,
    priceMode: item.priceMode || (item.priceIncludesGst ? PRICE_MODES.INCLUSIVE : PRICE_MODES.EXCLUSIVE),
});

const normalisePaymentsFromDb = (p = {}) => {
    const prepaidCards = Array.isArray(p.prepaidCards) && p.prepaidCards.length
        ? p.prepaidCards.map((c) => ({ cardId: String(c.cardId), amount: Number(c.amount) || 0 }))
        : p.prepaidCardId && Number(p.prepaidCardAmount) > 0
            ? [{ cardId: String(p.prepaidCardId), amount: Number(p.prepaidCardAmount) }]
            : [];
    return {
        ...emptyPayments(),
        cash: p.cash || '',
        card: p.card || '',
        upi: p.upi || '',
        wallet: p.wallet || '',
        prepaidCards,
        prepaidCardAmount: prepaidCards.reduce((s, c) => s + c.amount, 0),
        prepaidCardId: prepaidCards[0]?.cardId || null,
    };
};

const resolvePricingSettings = (responseData = {}) => {
    const tax = responseData.tax || {};
    const pricelist = responseData.pricelist || {};

    const gstRate = clampRate(pricelist.gstRate ?? pricelist.defaultGstRate ?? tax.defaultGstRate ?? 0, 0);
    const priceMode = normalisePriceMode(pricelist.priceMode ?? pricelist.defaultPriceMode ?? tax.defaultPriceMode ?? PRICE_MODES.EXCLUSIVE, PRICE_MODES.EXCLUSIVE);
    const gstin = String(tax.gstin || responseData.profile?.gstin || '').trim().toUpperCase();

    const registrationType = tax.registrationType || 'REGISTERED';
    const hasExplicitBillToggle = pricelist.gstBillEnabled !== undefined || tax.gstBillEnabled !== undefined;
    const legacyGstEnabled = pricelist.gstEnabled ?? tax.gstEnabled ?? false;
    const gstBillEnabled = hasExplicitBillToggle ? Boolean(pricelist.gstBillEnabled ?? tax.gstBillEnabled) : Boolean(legacyGstEnabled && hasValidGstin(gstin));
    const isGstBill = gstBillEnabled && registrationType !== 'UNREGISTERED' && hasValidGstin(gstin);

    return {
        priceMode, gstRate, gstBillEnabled, gstEnabled: gstBillEnabled,
        allowServiceOverride: Boolean(pricelist.allowServiceOverride ?? tax.allowServiceOverride),
        gstin, registrationType, isGstBill, gstApplicable: isGstBill && gstRate > 0,
    };
};

/** DB invoice -> screen session (no network calls here, so the list loads fast) */
const toSession = (dbDraft) => ({
    id: String(dbDraft._id || dbDraft.id),
    status: dbDraft.status || 'draft',
    storeId: dbDraft.storeId,
    partnerId: dbDraft.partnerId,
    createdAt: dbDraft.createdAt,
    totals: dbDraft.summary || { grandTotal: 0 },
    invoiceMeta: {
        invoiceNo: dbDraft.invoiceNo,
        date: dbDraft.date ? getLocalYYYYMMDD(dbDraft.date) : todayISO(),
        dueDate: dbDraft.dueDate ? getLocalYYYYMMDD(dbDraft.dueDate) : todayISO(),
    },
    customer: {
        customerId: dbDraft.customer?.customerId || null,
        phone: dbDraft.customer?.phone || '',
        name: dbDraft.customer?.name === 'Draft Customer' ? '' : (dbDraft.customer?.name || ''),
        walletBalance: 0,
        dueAmount: 0,
        activePackages: [],
        activePrepaidCards: [],
        prepaidMemberships: [],
        discountMemberships: [],
        _enriched: false,
    },
    items: dbDraft.items?.length > 0 ? dbDraft.items.map(normaliseInvoiceItem) : [getEmptyRow(), getEmptyRow()],
    overallDiscount: dbDraft.overallDiscount || emptyOverallDiscount(),
    isRoundOff: dbDraft.isRoundOff !== false,
    payments: normalisePaymentsFromDb(dbDraft.payments || {}),
    appliedMembershipId: '',          // '' = automatic (most expensive), 'NONE' = no discount
    membershipKeyApplied: undefined,  // last membership applied to rows
});

const CreateBill = () => {
    const navigate = useNavigate();
    const { id: urlDraftId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, hasPermission, constraint } = useAuth();
    const { activeStore } = useStore();
    const token = localStorage.getItem('smiify_token');
    const storeId = activeStore?._id || activeStore?.id;
    const safeUrlId = urlDraftId ? String(urlDraftId) : null;

    const [sessions, setSessions] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState(safeUrlId);
    const [completedMsg, setCompletedMsg] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isLoadingDrafts, setIsLoadingDrafts] = useState(true);
    const [pricingSettings, setPricingSettings] = useState(DEFAULT_PRICING_SETTINGS);
    const [walletByCustomer, setWalletByCustomer] = useState({});

    const [draftToCancel, setDraftToCancel] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);

    const sessionsRef = useRef(sessions);
    sessionsRef.current = sessions;
    const enrichingRef = useRef(new Set());
    const walletLoadingRef = useRef(new Set());

    // Live access (own access limited by the HQ branch limit). The server checks the same rules.
    const isOwner = user?.role === 'SUPERADMIN';
    const canCreateBill = hasPermission('billing', 'create');
    const canEditCompleted = hasPermission('billing', 'edit');
    const hasCancelAccess = hasPermission('billing', 'cancel_draft');
    // "Change bill date" switch (or the older "Can backdate bills" rule); the back-date limit still applies
    const hasDateChangeAccess = isOwner || hasPermission('billing', 'change_bill_date') || constraint('billing', 'allowDateChange', false) === true;
    // Bill discount needs: "Give manual discount" switch + "Can give discounts" rule + a max above 0%.
    // Same three checks as the server. The reason is shown on screen when one is missing.
    const maxDiscountPercent = isOwner ? 100 : Math.max(0, Math.min(100, Number(constraint('billing', 'maxDiscountLimit', 100)) || 0));
    const discountBlockedReason = isOwner ? ''
        : !hasPermission('billing', 'apply_discount') ? 'Turn on "Give manual discount" in Billing > Options.'
            : constraint('billing', 'allowDiscounting', true) === false ? 'Turn on "Can give discounts" in Billing > Options > Limits.'
                : maxDiscountPercent <= 0 ? '"Max manual discount" is 0%. Raise it in Billing > Options > Limits.'
                    : '';
    const hasDiscountAccess = !discountBlockedReason;

    const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}`, 'x-store-id': storeId }), [token, storeId]);

    /* ------------------------------ Pricing settings ------------------------------ */
    useEffect(() => {
        if (!storeId) { setPricingSettings(DEFAULT_PRICING_SETTINGS); return undefined; }
        let isMounted = true;
        (async () => {
            try {
                const response = await fetch(`${SETTINGS_API}?storeId=${encodeURIComponent(storeId)}`, { headers: { Authorization: `Bearer ${token}` } });
                const result = await response.json();
                if (response.ok && result.success && isMounted) setPricingSettings(resolvePricingSettings(result.data || {}));
            } catch (error) { console.error('Pricing settings load failed:', error); }
        })();
        return () => { isMounted = false; };
    }, [storeId, token]);

    /* ------------------------------ Drafts: load ONCE per store ------------------------------ */
    const loadedStoreRef = useRef(null);
    useEffect(() => {
        if (!token || !user || !storeId) { setIsLoadingDrafts(false); return undefined; }
        // another branch: bills of the old branch must not stay on screen
        if (loadedStoreRef.current && loadedStoreRef.current !== String(storeId)) {
            setSessions([]);
            setActiveSessionId(null);
            setWalletByCustomer({});
        }
        loadedStoreRef.current = String(storeId);
        let alive = true;
        (async () => {
            setIsLoadingDrafts(true);
            try {
                const res = await fetch(`${API_BASE}?status=draft`, { headers: authHeaders });
                const json = await res.json().catch(() => ({}));
                if (!alive) return;
                const drafts = res.ok && Array.isArray(json.data) ? json.data.map(toSession) : [];
                setSessions((prev) => {
                    // keep any non-draft session already opened (e.g. a completed bill from the URL)
                    const extra = prev.filter((s) => !drafts.some((d) => d.id === s.id) && String(s.storeId || storeId) === String(storeId));
                    return [...drafts, ...extra];
                });
            } catch (error) {
                console.error('Drafts fetch failed:', error);
            } finally {
                if (alive) setIsLoadingDrafts(false);
            }
        })();
        return () => { alive = false; };
    }, [storeId, token, user, authHeaders]);

    /* ------------------------------ URL -> active bill ------------------------------ */
    useEffect(() => {
        if (isLoadingDrafts) return undefined;

        if (!safeUrlId) {
            const first = sessionsRef.current.find((s) => s.status === 'draft') || sessionsRef.current[0];
            if (first) navigate(`/create-bill/${first.id}`, { replace: true });
            else setActiveSessionId(null);
            return undefined;
        }

        setActiveSessionId(safeUrlId);
        if (sessionsRef.current.some((s) => s.id === safeUrlId)) return undefined;

        // A bill that is not in the draft list (completed bill opened for editing)
        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/${safeUrlId}`, { headers: authHeaders });
                const json = await res.json().catch(() => ({}));
                const invoice = json?.data?.invoice || json?.data;
                if (!alive) return;
                if (res.ok && invoice && (invoice._id || invoice.id) && (!invoice.storeId || String(invoice.storeId) === String(storeId))) {
                    setSessions((prev) => (prev.some((s) => s.id === safeUrlId) ? prev : [...prev, toSession(invoice)]));
                } else {
                    navigate('/create-bill', { replace: true });
                }
            } catch (error) {
                console.error('Bill fetch failed:', error);
            }
        })();
        return () => { alive = false; };
    }, [safeUrlId, isLoadingDrafts, authHeaders, navigate]);

    /* ------------------------------ Session helpers ------------------------------ */
    const activeSession = sessions.find((s) => String(s.id) === String(activeSessionId)) || null;

    const updateSession = useCallback((sessionId, key, value) => {
        setSessions((previous) => {
            let changed = false;
            const next = previous.map((session) => {
                if (String(session.id) !== String(sessionId)) return session;
                const nextValue = typeof value === 'function' ? value(session[key]) : value;
                if (Object.is(nextValue, session[key])) return session;
                changed = true;
                return { ...session, [key]: nextValue };
            });
            return changed ? next : previous;
        });
    }, []);

    const updateActiveSession = useCallback((key, value) => updateSession(activeSessionId, key, value), [updateSession, activeSessionId]);

    const setCustomer = useCallback((value) => updateActiveSession('customer', value), [updateActiveSession]);
    const setInvoiceMeta = useCallback((value) => updateActiveSession('invoiceMeta', value), [updateActiveSession]);
    const setItems = useCallback((value) => updateActiveSession('items', value), [updateActiveSession]);
    const setOverallDiscount = useCallback((value) => updateActiveSession('overallDiscount', value), [updateActiveSession]);
    const setIsRoundOff = useCallback((value) => updateActiveSession('isRoundOff', value), [updateActiveSession]);
    const setPayments = useCallback((value) => updateActiveSession('payments', value), [updateActiveSession]);

    /* ------------------------------ Customer details: only for the open bill ------------------------------ */
    useEffect(() => {
        const s = activeSession;
        if (!s || s.customer?._enriched || !s.customer?.phone || s.customer.phone.length < 10) return;
        const key = `${s.id}:${s.customer.phone}`;
        if (enrichingRef.current.has(key)) return;
        enrichingRef.current.add(key);

        (async () => {
            try {
                const res = await fetch(`${CUSTOMER_API}/search?query=${encodeURIComponent(s.customer.phone)}`, { headers: authHeaders });
                const json = await res.json().catch(() => ({}));
                const live = (json.data || []).find((c) => c.phone === s.customer.phone) || (json.data || [])[0];
                updateSession(s.id, 'customer', (prev) => ({
                    ...prev,
                    ...(live ? {
                        customerId: live._id || live.id,
                        walletBalance: Number(live.walletBalance) || 0,
                        dueAmount: Number(live.dueAmount) || 0,
                        activePackages: live.activePackages || [],
                        activePrepaidCards: live.activePrepaidCards || [],
                        prepaidMemberships: live.prepaidMemberships || [],
                        discountMemberships: live.discountMemberships || [],
                    } : {}),
                    _enriched: true,
                }));
            } catch (error) {
                console.error('Customer details load failed', error);
            }
        })();
    }, [activeSession, authHeaders, updateSession]);

    /* ------------------------------ Membership wallet (all cards + discount plans) ------------------------------ */
    const activeCustomerId = activeSession?.customer?.customerId ? String(activeSession.customer.customerId) : '';

    const loadWallet = useCallback(async (customerId, force = false) => {
        if (!customerId || !storeId) return;
        if (!force && (walletByCustomer[customerId] !== undefined || walletLoadingRef.current.has(customerId))) return;
        walletLoadingRef.current.add(customerId);
        try {
            const res = await fetch(`${MEMBERSHIP_API}/customers/${customerId}/wallet`, { headers: authHeaders });
            const json = await res.json().catch(() => ({}));
            // false = could not load (screen falls back to customer search data, no endless spinner)
            setWalletByCustomer((m) => ({ ...m, [customerId]: res.ok && json.success ? json.data : false }));
        } catch (error) {
            console.error('Membership wallet load failed', error);
            setWalletByCustomer((m) => ({ ...m, [customerId]: false }));
        } finally {
            walletLoadingRef.current.delete(customerId);
        }
    }, [authHeaders, storeId, walletByCustomer]);

    useEffect(() => { loadWallet(activeCustomerId); }, [activeCustomerId, loadWallet]);

    const wallet = activeCustomerId ? walletByCustomer[activeCustomerId] : null;
    const prepaidCards = useMemo(() => normaliseCards(wallet, activeSession?.customer), [wallet, activeSession?.customer]);
    const activeMemberships = useMemo(() => sortMemberships(wallet?.discounts || []), [wallet]);
    // Client packages (services left, history) from the membership wallet
    // Only packages that are active and still have services left are shown in billing
    const clientPackages = useMemo(
        () => (wallet && Array.isArray(wallet.packages) ? wallet.packages : []).filter((p) => p.status === 'ACTIVE' && Number(p.totalLeft) > 0),
        [wallet]
    );
    // How many of each package service this bill already uses: "packageId|serviceKey" -> qty
    const packageUse = useMemo(() => {
        const m = {};
        (activeSession?.items || []).forEach((it) => {
            const pr = it.packageRedeem;
            if (pr?.packageId && pr?.serviceKey) {
                const k = `${pr.packageId}|${pr.serviceKey}`;
                m[k] = (m[k] || 0) + (Number(it.qty) || 1);
            }
        });
        return m;
    }, [activeSession?.items]);

    /** "Use" on a package service: adds a free line (price and GST were paid when the package was bought) */
    const handleUsePackage = useCallback((pkg, svc) => {
        if (!activeSession || !pkg || !svc) return;
        if (activeSession.status !== 'draft') { alert('Package services can be used on new bills only.'); return; }
        if (pkg.status !== 'ACTIVE') { alert(`Package "${pkg.name}" is ${pkg.status === 'EXPIRED' ? 'expired' : 'used up'}.`); return; }
        const key = `${pkg.id}|${svc.key}`;
        if ((packageUse[key] || 0) >= svc.left) { alert(`No "${svc.name}" left in "${pkg.name}".`); return; }
        updateSession(activeSession.id, 'items', (rows) => {
            const list = [...(rows || [])];
            const existing = list.findIndex((r) => r.packageRedeem && `${r.packageRedeem.packageId}|${r.packageRedeem.serviceKey}` === key);
            if (existing >= 0) {
                list[existing] = { ...list[existing], qty: (Number(list[existing].qty) || 1) + 1 };
                return list;
            }
            const line = {
                ...getEmptyRow(),
                itemId: svc.serviceId || null,
                itemName: svc.name,
                itemType: 'service',
                qty: 1,
                rate: 0,
                discountValue: '',
                gstRate: 0,
                taxPercent: 0,
                packageRedeem: { packageId: pkg.id, serviceKey: svc.key, packageName: pkg.name },
            };
            const blank = list.findIndex((r) => !String(r.itemName || '').trim());
            if (blank >= 0) list[blank] = { ...line, id: list[blank].id };
            else list.push(line);
            if (!list.some((r) => !String(r.itemName || '').trim())) list.push(getEmptyRow());
            return list;
        });
    }, [activeSession, packageUse, updateSession]);

    const appliedMembership = useMemo(() => {
        const choice = activeSession?.appliedMembershipId || '';
        if (choice === 'NONE') return null;
        return activeMemberships.find((m) => m.id === choice) || activeMemberships[0] || null;
    }, [activeSession?.appliedMembershipId, activeMemberships]);

    // When the membership in use changes, re-price rows (only open drafts; never silently change a completed bill)
    const membershipKey = appliedMembership ? appliedMembership.id : 'NONE';
    useEffect(() => {
        if (!activeSession || !wallet) return;
        if (activeSession.membershipKeyApplied === undefined) {
            updateSession(activeSession.id, 'membershipKeyApplied', membershipKey); // first load: just remember
            return;
        }
        if (activeSession.membershipKeyApplied === membershipKey) return;
        if (activeSession.status === 'draft') {
            updateSession(activeSession.id, 'items', (rows) => applyMembershipToItems(rows, appliedMembership));
        }
        updateSession(activeSession.id, 'membershipKeyApplied', membershipKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [membershipKey, wallet, activeSession?.id]);

    const handleMembershipChange = (membershipId) => updateActiveSession('appliedMembershipId', membershipId);

    /* ------------------------------ Actions ------------------------------ */
    const handleStartNewBill = async (prefill = null) => {
        if (!token || !user) return alert('Please login again.');
        if (!storeId || storeId === 'global_hq') return alert('Select a branch first.');
        if (!canCreateBill) return alert('You do not have access to create bills.');
        if (isCreating) return undefined;
        setIsCreating(true);
        try {
            // the server gives the invoice number (DD MM YY + store code + monthly sequence)
            const payload = { date: todayISO(), storeId, partnerId: user.partnerId || user.partner };
            const response = await fetch(`${API_BASE}/draft`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
                body: JSON.stringify(payload),
            });
            const responseData = await response.json().catch(() => ({}));
            if (!response.ok || !responseData.success) throw new Error(responseData.message || 'Could not start a new bill.');

            // Add locally and switch instantly (no page reload, no refetch of every draft)
            let session = toSession(responseData.data);
            if (prefill && typeof prefill === 'object' && !prefill.nativeEvent) {
                session = {
                    ...session,
                    ...(prefill.customer ? { customer: { ...session.customer, ...prefill.customer, _enriched: false } } : {}),
                    ...(prefill.items ? { items: [...prefill.items, getEmptyRow()] } : {}),
                };
            }
            setSessions((prev) => [session, ...prev.filter((s) => s.id !== session.id)]);
            navigate(`/create-bill/${session.id}`);
        } catch (error) {
            alert(error.message);
        } finally {
            setIsCreating(false);
        }
        return undefined;
    };

    const handleOpenDraft = (draft) => navigate(`/create-bill/${draft.id}`);

    const requestCancelDraft = (event, idToClose) => {
        event.stopPropagation();
        if (!hasCancelAccess) return alert('You do not have access to cancel draft bills.');
        setDraftToCancel(idToClose);
        return undefined;
    };

    const confirmCancelDraft = async () => {
        if (!draftToCancel) return;
        setIsCancelling(true);
        try {
            const response = await fetch(`${API_BASE}/${draftToCancel}/cancel`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                const remaining = sessions.filter((s) => s.id !== String(draftToCancel));
                setSessions(remaining);
                if (activeSessionId === String(draftToCancel)) {
                    const next = remaining.find((s) => s.status === 'draft');
                    navigate(next ? `/create-bill/${next.id}` : '/create-bill', { replace: true });
                }
            } else {
                alert(data.message || 'Failed to cancel draft.');
            }
        } catch (error) {
            console.error('Cancel API Error:', error);
        } finally {
            setIsCancelling(false);
            setDraftToCancel(null);
        }
    };

    /* ------------------------------ Buy a package again (from the membership desk) ------------------------------ */
    const prefillDone = useRef(false);
    useEffect(() => {
        const packageTemplateId = searchParams.get('package');
        if (!packageTemplateId || prefillDone.current || isLoadingDrafts || !storeId || storeId === 'global_hq' || !canCreateBill) return;
        prefillDone.current = true;
        (async () => {
            try {
                const res = await fetch(`${CATALOG_API_BASE}/packages/pos/package?storeId=${encodeURIComponent(storeId)}`, { headers: authHeaders });
                const json = await res.json().catch(() => ({}));
                const pkg = (json.data || []).find((p) => String(p.id) === String(packageTemplateId));
                if (!pkg) { alert('This package is not on sale any more.'); return; }
                await handleStartNewBill({
                    customer: {
                        customerId: searchParams.get('customerId') || null,
                        phone: (searchParams.get('phone') || '').replace(/\D/g, '').slice(-10),
                        name: searchParams.get('name') || '',
                    },
                    items: [{
                        ...getEmptyRow(), itemId: pkg.id, itemName: pkg.name, itemType: 'package', qty: 1, maxQty: 1,
                        rate: Number(pkg.netPayable) || 0, gstRate: Number(pkg.gstRate) || 0, taxPercent: Number(pkg.gstRate) || 0,
                        priceMode: PRICE_MODES.EXCLUSIVE,
                    }],
                });
            } catch (e) {
                console.error('Package prefill failed:', e);
            } finally {
                setSearchParams({}, { replace: true });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, isLoadingDrafts, storeId, canCreateBill]);

    /* ------------------------------ Totals ------------------------------ */
    const totals = useMemo(() => {
        if (!activeSession) return null;

        let totalQty = 0; let sumRate = 0; let sumGross = 0; let sumItemDiscount = 0; let sumTaxable = 0; let sumTax = 0; let sumRowAmount = 0;
        let serviceOnlyAmount = 0;

        const payments = activeSession.payments || emptyPayments();
        const isPrepaidActive = Number(payments.prepaidCardAmount) > 0 || Number(payments.wallet) > 0;

        const rows = (activeSession.items || []).map((item) => {
            const quantity = Math.max(0, Number(item.qty) || 0);
            const enteredRate = Math.max(0, Number(item.rate) || 0);

            const itemType = String(item.itemType || '').toLowerCase();
            const isProduct = itemType === 'product';
            const isLoyalty = LOYALTY_TYPES.includes(itemType);
            const isServiceLike = !isProduct && !isLoyalty;

            let gstRate = 0;
            let priceMode = item.priceMode;

            if (isProduct || isLoyalty) {
                gstRate = clampRate(item.gstRate ?? item.taxPercent ?? 0);
                priceMode = PRICE_MODES.EXCLUSIVE;
            } else {
                if (isPrepaidActive) {
                    gstRate = 0;
                } else {
                    const storedGstRate = item.gstRate ?? item.taxPercent;
                    const hasStoredGstRate = storedGstRate !== undefined && storedGstRate !== null && storedGstRate !== '';
                    gstRate = pricingSettings.isGstBill
                        ? (hasStoredGstRate ? clampRate(storedGstRate) : pricingSettings.gstRate)
                        : (hasStoredGstRate ? clampRate(storedGstRate) : 0);
                }
                priceMode = pricingSettings.allowServiceOverride
                    ? normalisePriceMode(item.priceMode || (item.priceIncludesGst ? PRICE_MODES.INCLUSIVE : pricingSettings.priceMode), pricingSettings.priceMode)
                    : pricingSettings.priceMode;
            }

            const pricing = calculatePricing({
                price: enteredRate, priceMode, gstRate, gstApplicable: gstRate > 0, qty: quantity,
                discountActive: Number(item.discountValue) > 0, discountType: item.discountType === '₹' ? 'Flat' : 'Percentage', discountValue: item.discountValue,
            });

            totalQty += quantity; sumRate += pricing.taxableUnitPrice * quantity; sumGross += enteredRate * quantity;
            sumItemDiscount += pricing.discountAmount; sumTaxable += pricing.taxableAmount; sumTax += pricing.gstAmount; sumRowAmount += pricing.finalAmount;
            if (isServiceLike) serviceOnlyAmount += pricing.finalAmount;

            return { id: item.id, pricing, grossAmount: enteredRate * quantity };
        });

        const overallDiscount = (hasDiscountAccess && activeSession.overallDiscount) ? activeSession.overallDiscount : emptyOverallDiscount();
        const overallDiscountValue = Math.max(0, Number(overallDiscount.value) || 0);
        const finalOverallDisc = overallDiscount.type === '%'
            ? (sumRowAmount * Math.min(overallDiscountValue, 100)) / 100
            : Math.min(sumRowAmount, overallDiscountValue);
        const discountPercent = sumRowAmount > 0 ? (finalOverallDisc / sumRowAmount) * 100 : 0;
        const discountOverLimit = finalOverallDisc > 0 && discountPercent > maxDiscountPercent + 0.01;

        const preRoundTotal = Math.max(0, sumRowAmount - finalOverallDisc);
        const grandTotal = activeSession.isRoundOff ? Math.round(preRoundTotal) : preRoundTotal;
        const roundOffAmt = activeSession.isRoundOff ? grandTotal - preRoundTotal : 0;

        const totalPaid = (Number(payments.cash) || 0) + (Number(payments.card) || 0) + (Number(payments.upi) || 0)
            + (Number(payments.wallet) || 0) + (Number(payments.prepaidCardAmount) || 0);
        const difference = totalPaid - grandTotal;

        let paymentStatus = 'pending';
        if (grandTotal > 0) {
            if (totalPaid <= 0) paymentStatus = 'pending';
            else if (difference < -0.01) paymentStatus = 'partial';
            else if (Math.abs(difference) <= 0.01) paymentStatus = 'full';
            else paymentStatus = 'over';
        }

        const serviceOnlyFinal = sumRowAmount > 0
            ? Math.max(0, serviceOnlyAmount - ((serviceOnlyAmount / sumRowAmount) * finalOverallDisc))
            : serviceOnlyAmount;

        return {
            rows, totalQty, sumRate, sumGross, sumItemDiscount, sumTaxable, sumTax, sumRowAmount, finalOverallDisc, preRoundTotal, roundOffAmt,
            grandTotal, totalPaid, balanceDue: grandTotal - totalPaid, paymentStatus,
            gstApplicable: sumTax > 0, gstin: pricingSettings.gstin, gstRate: pricingSettings.gstRate, priceMode: pricingSettings.priceMode,
            gstBillEnabled: pricingSettings.gstBillEnabled, isGstBill: pricingSettings.isGstBill,
            serviceOnlyTotal: Math.round(serviceOnlyFinal * 100) / 100, isPrepaidActive,
            discountPercent, discountOverLimit,
        };
    }, [activeSession, pricingSettings, hasDiscountAccess, maxDiscountPercent]);

    const isCompletedBill = Boolean(activeSession && activeSession.status !== 'draft');
    const readOnlyBill = isCompletedBill && !canEditCompleted;

    /* ------------------------------ Save ------------------------------ */
    const buildPayload = () => {
        const validItems = activeSession.items
            .filter((item) => String(item.itemName || '').trim() !== '')
            .map(({ discountSource, ...row }) => row);
        return {
            validItems,
            payload: {
                invoiceMeta: activeSession.invoiceMeta,
                customer: {
                    customerId: activeSession.customer?.customerId || null,
                    phone: activeSession.customer?.phone || '',
                    name: activeSession.customer?.name || '',
                },
                items: validItems,
                overallDiscount: activeSession.overallDiscount,
                isRoundOff: activeSession.isRoundOff,
                totals,
                payments: activeSession.payments,
                pricingSettings,
                status: activeSession.status,
            },
        };
    };

    const saveBill = async ({ preview }) => {
        if (!activeSession || !totals) return;
        const { validItems, payload } = buildPayload();

        if (readOnlyBill) { alert('You do not have access to edit completed bills.'); return; }
        if (totals.discountOverLimit) { alert(`Discount is above your limit of ${maxDiscountPercent}%.`); return; }
        if (preview && validItems.length === 0) { alert('Please add items to bill.'); return; }
        const hasLoyaltyItem = validItems.some((i) => LOYALTY_TYPES.includes(String(i.itemType || '').toLowerCase()));
        if (hasLoyaltyItem && !activeSession.customer?.phone) {
            alert('A customer phone number is required to sell a package or membership.');
            return;
        }
        if (preview && !activeSession.customer?.phone && !window.confirm('Customer phone number is missing. Save anyway as walk-in?')) return;

        setIsSaving(true);
        try {
            const response = await fetch(`${API_BASE}/${activeSession.id}/save-draft`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
                body: JSON.stringify(payload),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) { alert(`Could not save: ${data.message || response.status}`); return; }

            if (activeCustomerId) loadWallet(activeCustomerId, true); // balances may have changed (edited completed bill)
            if (preview) {
                navigate(`/create-bill/${activeSession.id}/preview`);
            } else {
                setCompletedMsg('Saved successfully.');
                setTimeout(() => setCompletedMsg(null), 3000);
            }
        } catch (error) {
            alert(`Could not save: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    /* ------------------------------ Render ------------------------------ */
    return (
        <div className={styles.appShell}>
            <BillActionStrip
                drafts={sessions.filter((s) => s.status === 'draft' || s.id === activeSessionId)}
                currentDraftId={activeSessionId}
                handleStartNewBill={handleStartNewBill}
                handleOpenDraft={handleOpenDraft}
                handleDeleteDraft={requestCancelDraft}
                hasCancelAccess={hasCancelAccess}
                canCreate={canCreateBill}
            />
            <div className={styles.pageWrapper}>
                <div className={styles.workspace}>
                    {!activeSession ? (
                        <div className={styles.emptyWorkspace}>
                            {isLoadingDrafts || (safeUrlId && !activeSession) ? (
                                <>
                                    <Loader2 size={32} className={styles.emptyIcon} />
                                    <h3>Opening bill...</h3>
                                </>
                            ) : (
                                <>
                                    <Store size={48} className={styles.emptyIcon} />
                                    <h3>POS Terminal Ready</h3>
                                    {canCreateBill ? (
                                        <button className={styles.primaryBtn} style={{ marginTop: '16px' }} onClick={() => handleStartNewBill()} disabled={isCreating}>
                                            <Plus size={14} /> {isCreating ? 'Starting...' : 'Start New Bill'}
                                        </button>
                                    ) : (
                                        <p style={{ marginTop: '12px', fontSize: '12px', fontWeight: 700, color: 'var(--h-color-neutral-500)' }}>You do not have access to create bills.</p>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className={styles.canvasWrapper}>
                            <div className={styles.billSplitLayout}>
                                <div className={styles.invoiceDocument}>
                                    <InvoiceHeader
                                        customer={activeSession.customer}
                                        setCustomer={setCustomer}
                                        invoiceMeta={activeSession.invoiceMeta}
                                        setInvoiceMeta={setInvoiceMeta}
                                        pricingSettings={pricingSettings}
                                        hasDateChangeAccess={hasDateChangeAccess}
                                        wallet={wallet}
                                        appliedMembership={appliedMembership}
                                        membershipDeskPath={MEMBERSHIP_DESK_PATH}
                                        packages={clientPackages}
                                        packageUse={packageUse}
                                        onUsePackage={activeSession.status === 'draft' ? handleUsePackage : null}
                                    />
                                    <InvoiceGrid
                                        customer={activeSession.customer}
                                        items={activeSession.items}
                                        setItems={setItems}
                                        getEmptyRow={getEmptyRow}
                                        totals={totals}
                                        catalogApiBase={CATALOG_API_BASE}
                                        pricingSettings={pricingSettings}
                                        isPrepaidActive={totals.isPrepaidActive}
                                        appliedMembership={appliedMembership}
                                    />
                                </div>
                                <div className={styles.summarySidebar}>
                                    <InvoiceSummary
                                        key={activeSession.id}
                                        customer={activeSession.customer}
                                        payments={activeSession.payments}
                                        setPayments={setPayments}
                                        overallDiscount={activeSession.overallDiscount}
                                        setOverallDiscount={setOverallDiscount}
                                        isRoundOff={activeSession.isRoundOff}
                                        setIsRoundOff={setIsRoundOff}
                                        totals={totals}
                                        hasDiscountAccess={hasDiscountAccess}
                                        discountBlockedReason={discountBlockedReason}
                                        customerWalletBalance={activeSession.customer?.walletBalance || 0}
                                        customerDueAmount={activeSession.customer?.dueAmount || 0}
                                        customerPackages={clientPackages}
                                        prepaidCards={prepaidCards}
                                        memberships={activeMemberships}
                                        appliedMembership={appliedMembership}
                                        onMembershipChange={handleMembershipChange}
                                        membershipDeskPath={MEMBERSHIP_DESK_PATH}
                                        isEditingCompleted={activeSession.status !== 'draft'}
                                        maxDiscountPercent={maxDiscountPercent}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {activeSession && (
                <div className={styles.fixedFooter}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className={styles.sessionStatusText}>Status: <b>{activeSession.status.toUpperCase()}</b></span>
                        {completedMsg && <span className={styles.completedToast}><CheckCircle2 size={14} /> {completedMsg}</span>}
                        {readOnlyBill && <span style={{ color: 'var(--h-color-danger-600, #be1025)', fontSize: '11.5px', fontWeight: 800 }}>View only: you cannot edit completed bills.</span>}
                        {!readOnlyBill && totals?.discountOverLimit && <span style={{ color: 'var(--h-color-danger-600, #be1025)', fontSize: '11.5px', fontWeight: 800 }}>Discount {totals.discountPercent.toFixed(1)}% is above your limit of {maxDiscountPercent}%.</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        {readOnlyBill ? (
                            <button className={styles.primaryBtn} onClick={() => navigate(`/create-bill/${activeSession.id}/preview`)}><FileText size={14} /> View bill</button>
                        ) : (
                            <>
                                <button className={styles.secondaryBtn} onClick={() => saveBill({ preview: false })} disabled={isSaving || totals?.discountOverLimit}><FileText size={14} /> Save</button>
                                <button className={styles.primaryBtn} onClick={() => saveBill({ preview: true })} disabled={isSaving || totals?.discountOverLimit}><Save size={14} /> {isSaving ? 'Saving...' : 'Save & Preview'}</button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {draftToCancel && (
                <div className={styles.modalOverlay}>
                    <div className={styles.confirmModal}>
                        <div className={styles.modalHeader}><AlertCircle size={22} className={styles.alertIcon} /><h4>Cancel Draft Bill?</h4></div>
                        <p>Are you sure you want to cancel this bill? It will be marked as cancelled in the system and removed from this list.</p>
                        <div className={styles.modalActions}>
                            <button className={styles.cancelPopBtn} onClick={() => setDraftToCancel(null)} disabled={isCancelling}>No, Keep it</button>
                            <button className={styles.confirmPopBtn} onClick={confirmCancelDraft} disabled={isCancelling}>{isCancelling ? 'Cancelling...' : 'Yes, Cancel it'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateBill;
